import { router, Stack, useLocalSearchParams } from 'expo-router';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useFinishReservation } from '@/api/reservations/use-finish-reservation';
import { useReservation } from '@/api/reservations/use-reservation';
import BackButton from '@/components/back-button';
import ListItemStatus from '@/components/list-item-status';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Review } from '@/components/review';
import { Button, Text } from '@/ui';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Icon } from '@/ui/icon';
import { Car } from '@/ui/icons';
import { Bag } from '@/ui/icons/bag';
import { Bolt } from '@/ui/icons/bolt';
import { Call } from '@/ui/icons/call';
import { Camera } from '@/ui/icons/camera';
import { Hourglass } from '@/ui/icons/hourglass';
import { KeyCard } from '@/ui/icons/key-card';
import { Loader } from '@/ui/loader';
import { MenuItem } from '@/ui/menu-item';

export default function Dropoff() {
  const { styles, theme } = useStyles(stylesheet);
  const { id } = useLocalSearchParams();
  const [finishTripModalVisible, setFinishTripModalVisible] =
    React.useState(false);
  const scrollViewRef = React.useRef(null);

  const { mutateAsync: finishReservation, isPending: isFinishingReservation } =
    useFinishReservation();

  const items = useMemo(() => {
    return [
      {
        icon: (
          <Icon
            type="dressed"
            icon={<Bag />}
            fillColor={theme.colors.icon.color}
            width={24}
            height={24}
          />
        ),
        text: 'Take all your personal belongings',
      },
      {
        icon: (
          <Icon
            type="dressed"
            icon={<KeyCard />}
            fillColor={theme.colors.icon.color}
            width={24}
            height={24}
          />
        ),
        text: 'Leave the key in the car where you picked it up',
      },
      {
        icon: (
          <Icon
            type="dressed"
            icon={<Car />}
            fillColor={theme.colors.icon.color}
            width={24}
            height={24}
          />
        ),
        text: 'Park the car where you picked it up or contact the valet in the parking garage.',
      },
      {
        icon: (
          <Icon
            type="dressed"
            icon={<Bolt />}
            fillColor={theme.colors.icon.color}
            width={24}
            height={24}
          />
        ),
        text: 'Plug in the car (if it is possible)',
      },
      {
        icon: (
          <Icon
            type="dressed"
            icon={<Camera />}
            fillColor={theme.colors.icon.color}
            width={24}
            height={24}
          />
        ),
        text: 'Take post-trip photos to ensure you are not held liable for any damages that may occur after you return the car.',
      },
    ];
  }, []);

  const {
    data: resData,
    isPending: isFetchingReservation,
    refetch: refetchTrip,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id,
  });

  const handleConfirm = async () => {
    try {
      await finishReservation({ id: id });
      setFinishTripModalVisible(false);
      Toast.show({
        text1: 'Your trip has been completed.',
        type: 'success',
      });
      router.back();
    } catch (e) {}
  };

  if (isFetchingReservation) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <KeyboardAwareScrollView
      ref={scrollViewRef}
      contentContainerStyle={styles.parentContainer}
      style={{ flex: 1, backgroundColor: theme.colors.background_1 }}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.container}>
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <Stack.Screen
            options={{
              headerShown: false,
            }}
          />
        ) : (
          <Stack.Screen
            options={{
              title: 'Complete Your Drop-Off',
              headerTitle: 'Complete Your Drop-Off',
              headerLeft: () => <BackButton />,
              headerShown: true,
              headerStyle: {
                backgroundColor: theme.colors.background_1,
              },
              headerTintColor: theme.colors.text.primary,
              headerShadowVisible: false,
            }}
          />
        )}

        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16, marginBottom: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.back();
              }}
            />

            <Text type="largeTitle">Complete Your Drop-Off</Text>
          </View>
        ) : null}

        <View style={{ gap: 48 }}>
          <View style={{ gap: 24 }}>
            <View style={{ gap: 8 }}>
              <Text type="title2">Thank You For Renting With Us!</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Before leaving, please double-check the following:
              </Text>
            </View>

            <View>
              <ListItemStatus
                icon={
                  <Icon
                    type="naked"
                    icon={<Hourglass />}
                    fillColor={theme.colors.icon.color}
                    width={14}
                    height={14}
                  />
                }
                text={'Take Post-Trip Photos'}
                subtext={
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Recommended
                  </Text>
                }
                buttonText={'Take Photos'}
                onPress={() => {
                  router.push(`/trips/${resData.id}/upload-photos`);
                }}
              />
            </View>

            <View
              style={{
                height: 1,
                width: '100%',
                backgroundColor: theme.colors.card.default.borderColor,
              }}
            />

            <View style={{ gap: 16 }}>
              <Text type="title2">
                {resData.customReturnLocation
                  ? 'Drop Off Details'
                  : 'Drop Off Instructions'}
              </Text>

              <View>
                {resData.customReturnLocation ? (
                  <MenuItem
                    leftIcon={<Call />}
                    leftIconFillColor={theme.colors.icon.color}
                    tx="carDetails.deliveryTitle"
                    tx2="carDetails.deliveryBody"
                    values={{ phone: resData.car.ownerPhone }}
                    style={{ width: '100%' }}
                    disabled
                  />
                ) : (
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {resData.returnLocation.dropoffInstructions ||
                      resData.car.defaultLocation.dropoffInstructions}
                  </Text>
                )}
              </View>
            </View>

            <View
              style={{
                height: 1,
                width: '100%',
                backgroundColor: theme.colors.card.default.borderColor,
              }}
            />

            <View style={{ gap: 16 }}>
              {items.map((item, index) => {
                return (
                  <View
                    key={`item-${index}`}
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    {item.icon}

                    <View style={{ flex: 1 }}>
                      <Text type="bodyRegular">{item.text}</Text>
                    </View>
                  </View>
                );
              })}
            </View>

            <View
              style={{
                height: 1,
                width: '100%',
                backgroundColor: theme.colors.card.default.borderColor,
              }}
            />

            <Review id={resData.id} scrollViewRef={scrollViewRef} />
          </View>

          <View>
            <Button
              text="Finish Trip"
              type="primary"
              onPress={() => {
                setFinishTripModalVisible(true);
              }}
            />

            <Button
              text="Go Back"
              type="ghost"
              onPress={() => {
                router.back();
              }}
            />
          </View>
        </View>
      </View>

      <ConfirmModal
        isVisible={finishTripModalVisible}
        titleTx="dropoff.finishTripModalTitle"
        bodyTx="dropoff.finishTripModalBody"
        leftButton={{
          tx: 'dropoff.finishTripModalCancel',
          onPress: () => {
            setFinishTripModalVisible(false);
          },
        }}
        rightButton={{
          tx: 'dropoff.finishTripModalConfirm',
          onPress: handleConfirm,
        }}
        onDismiss={() => {
          setFinishTripModalVisible(false);
        }}
      />
    </KeyboardAwareScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  parentContainer: {
    flexGrow: 1,
    alignItems: 'center',
  },
  container: {
    flexGrow: 1,
    padding: 16,
    width: {
      xs: '100%',
      md: 600,
    },
    height: {
      md: 600,
    },
  },
}));
